import styles from './PlaqueTable.module.scss';
import React, { useEffect } from 'react';
import { ICON_MAP } from '@components/Icons/Icons';
import {
  Lesion,
  PlaqueBranch,
  PlaqueStatus,
} from 'config/config';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { configStore } from 'config/configStore';
import useModalActions from '@hooks/common/useModalAction';
import { useBranchInfo } from '@contexts/BranchContext';
import InputNameContextMenu from '@components/ContextMenu/inputNameContextMenu';
import {
  MarkerLinesMoveRender,
  useBufferPool,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import SimpleContextMenu, {
  SimpleContextMenuItem,
} from '@components/ContextMenu/SimpleContextMenu';

interface Props {
  width: number;
  height: number;
  columns: string[];
  rows: {
    label: string;
    values: string[];
  }[];
}

const MAX_VISIBLE = 3;

const PlaqueTable: React.FC<Props> = ({ width, height, columns, rows }) => {
  const CloseIcon = ICON_MAP['modalclose'];
  const { panelStates, updateCPRState } = usePanelContainer();
  const { errorDefaultAction, selectAction } = useModalActions();
  const { branchIds } = useBranchInfo();
  const { bufferPool } = useBufferPool();
  const { selectedSeriesId } = useBrowserPage();
  const panelState = panelStates['CPR'].coronary!;
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;

  const [selectedVessel, setSelectedVessel] = React.useState<{
    name: string;
    index: number;
  } | null>({
    name: 'Current',
    index: panelState.current[0],
  });
  const [selectedindex, setselectedindex] = React.useState<number>(-1);
  const [initLesionName, setInitLesionName] = React.useState<string>('');
  const [showSaveMenu, setShowSaveMenu] = React.useState(false);
  const [showVesselList, setShowVesselList] = React.useState(false);

  const plaqueStatus = configStore.get().plaqueStatus;
  const plaqueSummary = plaqueStatus.map((p) => ({
    vesselName: p.vesselName ?? `Vessel ${p.vesselIndex}`,
    vesselIndex: p.vesselIndex,
    count: p.lesions.length,
  }));
  const isPlaqueSummaryEmpty = plaqueSummary.length === 0;

  const handleSelectVessel = (vessel: { name: string; index: number }) => {
    if (vessel.name === 'Current') {
      setselectedindex(-1);
    }

    const nextVessel =
      vessel.name === 'Current'
        ? { name: 'Current', index: panelState.current[0] }
        : vessel;

    setSelectedVessel((prev) => {
      if (prev?.name === nextVessel.name && prev.index === nextVessel.index) {
        return prev;
      }
      return { name: nextVessel.name, index: nextVessel.index };
    });

    setselectedindex(-1);
  };

  const handleSelectLesion = (index: number) => {
    const lesion = selectedLesions[index];
    if (!lesion || selectedVessel === null) return;
    setselectedindex(index);

    updateCPRState({
      mainIndex: { index: 0, branchId: selectedVessel?.index! },
    });

    [0, 1, 2].forEach((dragIndex, i) => {
      updateCPRState({
        indexedState: {
          mainIndex: { branchId: selectedVessel?.index!, index: dragIndex },
          pickPoint: { index: lesion.value[i] },
        },
      });
    });

    requestAnimationFrame(() => {
      MarkerLinesMoveRender(
        0,
        selectedVessel?.index!,
        [0, 1, 2],
        lesion.value,
        pool!,
      );
    });
  };

  const handleSelectDeleteLesion = (index: number) => {
    const currentPlaqueStatus = configStore.get().plaqueStatus;
    if (selectedVessel === null) return;
    const targetBranch = currentPlaqueStatus.find(b => b.vesselIndex === selectedVessel.index);
    if (!targetBranch) return;

    const updatedLesions = targetBranch.lesions.filter((_, l) => l !== index);

    const updatedBranch: PlaqueBranch = {
      ...targetBranch,
      lesions: updatedLesions,
    };

    let updatedPlaqueStatus = currentPlaqueStatus.map((b) =>
      b.vesselIndex === selectedVessel?.index ? updatedBranch : b,
    );

    if (updatedLesions.length === 0) {
      updatedPlaqueStatus = updatedPlaqueStatus.filter(
        (b) => b.vesselIndex !== selectedVessel?.index,
      );
      setSelectedVessel({ name: 'Current', index: panelState.current[0] });
    }

    configStore.setData({ plaqueStatus: updatedPlaqueStatus });
    setselectedindex(-1);
  };

  useEffect(() => {
    setSelectedVessel((prev) =>
      prev?.name === 'Current'
        ? { name: 'Current', index: panelState.current[0] }
        : prev,
    );
  }, [panelState.current[0]]);

  useEffect(() => {
    if (selectedindex === -1) return;
    const branchIndex = panelState.current[0];
    const lesionIndex = panelState.state[branchIndex];
    if (!lesionIndex?.[0] || !lesionIndex?.[2] || !selectedLesions[selectedindex]) {
      setselectedindex(-1);
      return;
    }
    const check =
      selectedLesions[selectedindex].value[0] !== lesionIndex[0].index ||
      selectedLesions[selectedindex].value[2] !== lesionIndex[2].index;
    if (check) setselectedindex(-1);
  }, [panelState]);

  async function handleAddLesion(lesionName: string) {
    const branchIndex = panelState.current[0];
    const lesionIndex = panelState.state[branchIndex];
    if (!lesionIndex?.[0] || !lesionIndex?.[1] || !lesionIndex?.[2]) {
      errorDefaultAction('Current vessel marker data is not ready.');
      return;
    }

    const currentPlaqueStatus = configStore.get().plaqueStatus;
    const targetBranch = currentPlaqueStatus.find(
      (b) => b.vesselIndex === branchIndex,
    );

    const newLesion: Lesion = {
      name: `${lesionName}`,
      value: [lesionIndex[0].index, lesionIndex[1].index, lesionIndex[2].index],
    };

    let updatedPlaqueStatus: PlaqueStatus;

    // lesion 이름 중복 검사
    // if (targetBranch) {
    //   const isDuplicate = targetBranch.lesions.some(
    //     (l) =>
    //       l.name.trim().toLowerCase() === newLesion.name.trim().toLowerCase(),
    //   );

    //   if (isDuplicate) {
    //     errorDefaultAction(`Duplicate lesion name : ${newLesion.name}`);
    //     return;
    //   }
    // }

    if (targetBranch) {
      const updatedBranch: PlaqueBranch = {
        ...targetBranch,
        lesions: [...targetBranch.lesions, newLesion],
      };

      updatedPlaqueStatus = currentPlaqueStatus.map((b) =>
        b.vesselIndex === branchIndex ? updatedBranch : b,
      );
    } else {
      let name = '';
      const result = branchIds
        .slice(0)
        .find((obj) => Object.prototype.hasOwnProperty.call(obj, branchIndex));
      if (result) {
        let [id, label] = Object.entries(result)[0];
        name = label;
      }
      const newBranch: PlaqueBranch = {
        vesselName: name,
        vesselIndex: branchIndex,
        lesions: [newLesion],
      };

      updatedPlaqueStatus = [newBranch, ...currentPlaqueStatus];
    }

    configStore.setData({ plaqueStatus: updatedPlaqueStatus });
  }

  const handleOpenAddLesionMenu = () => {
    const branchIndex = panelState.current[0];
    const currentPlaqueStatus = configStore.get().plaqueStatus;
    const targetBranch = currentPlaqueStatus.find(
      (b) => b.vesselIndex === branchIndex,
    );

    const lesionCount = targetBranch ? targetBranch.lesions.length : 0;

    if (lesionCount >= 3) {
      errorDefaultAction('You can only add up to 3 lesions per vessel.');
      return;
    }

    setInitLesionName(`Lesion`);
    setShowSaveMenu(true);
  };

  const selectedLesions =
    selectedVessel && selectedVessel.name !== 'Current'
      ? (plaqueStatus.find((p) => p.vesselName === selectedVessel.name)
          ?.lesions ?? [])
      : [];

  const handleSwitchVesselOrder = (name: string) => {
    const currentPlaqueStatus = configStore.get().plaqueStatus;

    const target = currentPlaqueStatus.find((b) => b.vesselName === name);
    if (!target) return;

    const reordered = [
      target,
      ...currentPlaqueStatus.filter((b) => b.vesselName !== name),
    ];

    configStore.setData({ plaqueStatus: reordered });
  };

  // const vesselMenuItems: SimpleContextMenuItem[] = React.useMemo(() => {
  //   const currentPlaqueStatus = configStore.get().plaqueStatus;

  //   return currentPlaqueStatus.map((branch) => ({
  //     label: branch.vesselName ?? '',
  //     onClick: () => {
  //       handleSwitchVesselOrder(branch.vesselName!);
  //       setShowVesselList(false);
  //     },
  //   }));
  // }, [configStore.get().plaqueStatus]);

  const vesselMenuItems: SimpleContextMenuItem[] = React.useMemo(() => {
    const overflow = plaqueSummary.slice(MAX_VISIBLE); // 보이는 3개 제외
    return overflow.map(({ vesselName }) => ({
      label: vesselName ?? '',
      onClick: () => {
        handleSwitchVesselOrder(vesselName!); // 클릭 시 맨 앞으로 이동
        setShowVesselList(false);
      },
    }));
  }, [plaqueSummary]);

  return (
    <div className={styles.wrapper} style={{ width, height }}>
      <div className={styles.header}>
        <div
          className={`${styles.tab} ${selectedVessel?.name === 'Current' ? styles.active : ''} ${isPlaqueSummaryEmpty ? styles.tabDisabled : ''}`}
          onClick={
            isPlaqueSummaryEmpty
              ? undefined
              : () => handleSelectVessel({ name: 'Current', index: 0 })
          }
        >
          <span>Current View</span>
        </div>
        {plaqueSummary
          .slice(0, MAX_VISIBLE)
          .map(({ vesselName, vesselIndex, count }) => (
            <div
              key={vesselName}
              className={`${styles.tab} ${selectedVessel?.name === vesselName ? styles.active : ''}`}
              onClick={() =>
                handleSelectVessel({ name: vesselName, index: vesselIndex })
              }
            >
              <span>{vesselName}</span>
              <span className={styles.badge}>{count}</span>
            </div>
          ))}
        <div onClick={() => setShowVesselList(true)}>
          {plaqueSummary.length > MAX_VISIBLE && (
            <div className={styles.more}>···</div>
          )}
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.lesions}>
          {selectedVessel?.name === 'Current' ? (
            <div className={styles.addedContainer}>
              <div className={styles.added} onClick={handleOpenAddLesionMenu}>
                Add Lesion
              </div>
            </div>
          ) : (
            <div className={styles.lesionItems}>
              {selectedLesions?.map((lesion, index) => (
                <div
                  key={index}
                  className={`${styles.lesionItem} ${selectedindex === index ? styles.active : ''}`}
                  onClick={() => handleSelectLesion(index)}
                >
                  <span className={styles.name}>{lesion.name}</span>
                  <span className={styles.close}>
                    <CloseIcon
                      className={styles['base-modal-close']}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAction(
                          'Are you sure you want to delete the data for this lesion?',
                          () => handleSelectDeleteLesion(index),
                        );
                      }}
                    />
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th key={idx}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rIdx) => {
              const highlightClass = styles['highlight-row'];
              return (
                <tr key={rIdx} className={highlightClass}>
                  <td>{row.label}</td>
                  {row.values.map((val, cIdx) => (
                    <td key={cIdx}>{val}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {showSaveMenu && (
        <InputNameContextMenu
          title="New Lesion"
          initialValue={initLesionName}
          onSave={(value) => {
            handleAddLesion(value);
            setShowSaveMenu(false);
          }}
          onClose={() => setShowSaveMenu(false)}
        />
      )}
      {showVesselList && (
        <SimpleContextMenu
          items={vesselMenuItems}
          onClose={() => setShowVesselList(false)}
        />
      )}
    </div>
  );
};

export default PlaqueTable;
